import React, { useCallback, useContext, useRef } from 'react';
import { PageContentStateContext } from '@src/contexts/wysiwyg/page_content';
import useSetImageContent from '@src/hooks/wysiwyg_editor/useSetImageContent';
import { ImageCacheStateContext } from '@src/contexts/wysiwyg/image_cache';
import { TImageContentItem } from '@src/types/wysiwyg_editor';
import type { ImageData } from '@src/contexts/wysiwyg/image_cache';
import { isImage } from '@src/utils/wysiwyg_editor/data';
import { useImageGenerator } from '@src/hooks/wysiwyg_editor/useImageGenerator';
import { useDispatch } from 'react-redux';
import { addPageContentItem, saveSnapshot } from '@src/actions/wysiwyg/editor';

function ImageDropOverlay(): React.ReactElement {
  const dispatch = useDispatch();
  const imageGenerator = useImageGenerator();
  const { contentItems } = useContext(PageContentStateContext);
  const { dragging } = useContext(ImageCacheStateContext);
  const setImageContent = useSetImageContent();

  const handleDragOver = useCallback(
    (event: React.DragEvent<HTMLDivElement>) => {
      event.preventDefault();
    },
    []
  );

  const handleDragEnter = useCallback(
    (event: React.DragEvent<HTMLDivElement>) => {
      event.preventDefault();
      (event.target as HTMLDivElement).classList.add(
        'wysiwyg-editor-drag-target--active'
      );
    },
    []
  );

  const handleDragLeave = useCallback(
    (event: React.DragEvent<HTMLDivElement>) => {
      event.preventDefault();
      (event.target as HTMLDivElement).classList.remove(
        'wysiwyg-editor-drag-target--active'
      );
    },
    []
  );

  const handleTargetDrop = useCallback(
    (event: React.DragEvent<HTMLElement>, pageItem: TImageContentItem) => {
      event.preventDefault();
      event.stopPropagation();

      const { data } = JSON.parse(event.dataTransfer.getData('Text')) as {
        data: ImageData;
      };
      setImageContent(pageItem, data);
    },
    [setImageContent]
  );

  const handleOverlayDrop = useCallback(
    (event: React.DragEvent<HTMLDivElement>) => {
      event.preventDefault();
      event.stopPropagation();

      const { data, dragOffset } = JSON.parse(
        event.dataTransfer.getData('Text')
      ) as { data: ImageData; dragOffset: { x: number; y: number } };

      const overlayPosition = (event.target as HTMLDivElement).getBoundingClientRect();
      const dropX = event.clientX - overlayPosition.left;
      const dropY = event.clientY - overlayPosition.top;

      const newImageItem = imageGenerator({
        url: data.url,
        imageDimensions: data.naturalDimensions,
        hopefulLocation: {
          x: dropX,
          y: dropY
        },
        dragOffset
      });

      dispatch(saveSnapshot());
      dispatch(addPageContentItem(newImageItem));
    },
    [dispatch, imageGenerator]
  );

  if (!dragging) return null;

  return (
    <div
      style={{
        // image/text elements including the content and their drag handles use
        // indexes in the hundreds, so do our best to be higher
        zIndex: 1000
      }}
      className="wysiwyg-editor-drag-target--overlay"
      onDragOver={handleDragOver}
      onDragEnter={handleDragEnter}
      onDragLeave={handleDragLeave}
      onDrop={handleOverlayDrop}
    >
      {contentItems.map((item) => {
        if (!isImage(item)) return null;

        return (
          <div
            style={{
              position: 'absolute',
              left: `${item.containerLocation.x * 100}%`,
              top: `${item.containerLocation.y * 100}%`,
              height: `${item.containerLocation.height * 100}%`,
              width: `${item.containerLocation.width * 100}%`
            }}
            key={item.id}
            onDragOver={handleDragOver}
            onDragEnter={handleDragEnter}
            onDragLeave={handleDragLeave}
            onDrop={(e) => handleTargetDrop(e, item)}
          />
        );
      })}
    </div>
  );
}

export default ImageDropOverlay;
